/**
 * Catalogue of supported and coming-soon acquisition-analysis source formats.
 *
 * Single source of truth for the upload form dropdown AND the "currently
 * supported" status card on the page. Adding a new aggregator parser later
 * means appending one entry here + flipping `supported: true`.
 */

import type {
  AcquisitionSourceFormat,
  SourceFormatOption,
} from '../../types/acquisitionAnalysis';

export const SOURCE_FORMATS: SourceFormatOption[] = [
  {
    value: 'auto',
    label: 'Auto-detect (recommended)',
    description:
      'Backend detects the format from file content. Works for any of the formats below.',
    acceptedExtensions: ['.xlsx', '.xls', '.csv', '.pdf'],
    supported: true,
    hasBrokerLevelData: true,
  },
  {
    value: 'loan_market_rcti_pdf',
    label: 'Loan Market RCTI PDFs',
    description:
      'Multi-page Loan Market RCTI summaries (per-broker rows; "Subtotal Upfront/Trail Commission" markers).',
    acceptedExtensions: ['.pdf'],
    supported: true,
    hasBrokerLevelData: true,
  },
  {
    value: 'loan_market_summary_xlsx',
    label: 'Loan Market summary spreadsheet',
    description:
      'Excel/CSV with broker rows × month columns (e.g. "Jul 2023", "Aug 2023"). Multi-FY supported.',
    acceptedExtensions: ['.xlsx', '.xls', '.csv'],
    supported: true,
    hasBrokerLevelData: true,
  },
  {
    value: 'connective_rcti_pdf',
    label: 'Connective RCTI PDFs (entity-level)',
    description:
      'Single-page Connective RCTIs with "Commission Income Summary" + "Payments to 3rd Parties" tables. Broker-level breakdown is NOT included; broker tabs hide automatically.',
    acceptedExtensions: ['.pdf'],
    supported: true,
    hasBrokerLevelData: false,
  },
];

export function getSourceFormat(
  value: AcquisitionSourceFormat
): SourceFormatOption {
  const found = SOURCE_FORMATS.find((s) => s.value === value);
  if (!found) {
    throw new Error(`Unknown source format: ${value}`);
  }
  return found;
}

/**
 * Returns true if the file's extension is acceptable for the chosen source.
 */
export function isFileAllowedForSource(
  filename: string,
  format: AcquisitionSourceFormat
): boolean {
  const cfg = getSourceFormat(format);
  const lower = filename.toLowerCase();
  return cfg.acceptedExtensions.some((ext) => lower.endsWith(ext));
}
